/**
 * Milo (the assistant) as a fixture of the whole portal rather than a page you go to.
 *
 * The reception portal's file of the same name, copied (docs/AI.md § "Milo v portalu
 * sobaric"). The two portals share no package yet, so the duplication is deliberate and
 * recorded as a deviation; what differs here is written down below and nowhere else.
 *
 * # Milo in the top bar, and only for the supervisor
 *
 * The orb sits beside the language, theme and sign-out buttons and opens a drawer over
 * whatever screen is underneath — so "komu je dodeljena ta soba" is asked with the room
 * still on the screen, and the proposal is confirmed with it still there.
 *
 * There is NO full page here. The reception portal keeps `/pomocnik` for long work and
 * the "Vsi pogovori" oversight list; this portal is read on a telephone in a corridor,
 * where a second address for the same conversation would be a screen nobody navigates
 * to. The drawer therefore has no "Odpri celozaslonsko" button — a button that goes
 * nowhere is worse than no button.
 *
 * # What lives here and what does not
 *
 * Here: whether the drawer is open, which thread it has open, and the three things the
 * orb has to know — that a request is in flight, that something is waiting to be read,
 * that a proposal is waiting to be decided. All of it is about this browser at this
 * moment, so all of it is a module-level ref rather than a store: nothing here belongs
 * to the account, and nothing here is worth a Pinia action.
 *
 * Not here: the conversation itself (`components/AssistantConversation.vue`),
 * and what the page underneath is about (`libs/assistantContext.ts`).
 */
import { computed, onUnmounted, ref, watch } from 'vue'

import { Permission } from '@/enums/permission'
import { useAppStore } from '@/stores/app'
import type { MiloReaction, MiloState } from '@/types/Milo'

/** Where the drawer's open state and its thread are remembered, per device. */
const OPEN_KEY   = 'assistant.drawer.open'
const THREAD_KEY = 'assistant.drawer.thread'

const open     = ref(localStorage.getItem(OPEN_KEY) === '1')
const threadId = ref(localStorage.getItem(THREAD_KEY) ?? '')

/** A turn is in flight. */
const busy = ref(false)

/** An answer arrived that nobody has looked at — set when a turn lands with the drawer
 *  shut, cleared when it is opened. */
const unread = ref(false)

/** A proposal in the open thread is still waiting for Potrdi. */
const pending = ref(false)

/**
 * Milo's face, told what just happened.
 *
 * # Why a bus and not a ref to a component
 *
 * There is more than one head on screen — the top bar's and, once it has been opened,
 * the drawer header's — and a one-shot expression belongs on every one of them: they
 * are the same Milo, and a head that smiled while the other did not would read as two.
 * A template ref points at exactly one component and would also have to be threaded
 * from the conversation, through the drawer, to a component the conversation does not
 * own. Every mounted head subscribes here instead, and whoever has news simply says it.
 *
 * A reaction is fire-and-forget on purpose: business logic must not wait on an
 * animation, and a head that is still loading (or is the CSS orb, on a machine with no
 * WebGL) silently has nothing to play.
 */
type MiloReactionListener = (reaction: MiloReaction) => void

const miloListeners = new Set<MiloReactionListener>()

export const reactMilo = (reaction: MiloReaction): void => {
    for (const listener of miloListeners) listener(reaction)
}

/** Subscribes a head for as long as it is mounted. */
export const onMiloReaction = (listener: MiloReactionListener): void => {
    miloListeners.add(listener)

    onUnmounted(() => { miloListeners.delete(listener) })
}

watch(open, value => {
    localStorage.setItem(OPEN_KEY, value ? '1' : '0')

    if (!value) return

    unread.value = false
    // Opening the drawer is the one moment somebody has deliberately turned TO Milo.
    // A greeting there costs nothing and is the only reaction that is not about work.
    reactMilo('smile')
})

watch(threadId, value => localStorage.setItem(THREAD_KEY, value))

/**
 * What the orb is doing.
 *
 *   idle      — nothing to say.
 *   thinking  — a request is in flight.
 *   attention — a proposal awaits a decision, or an answer has not been read.
 *
 * Thinking outranks attention: what is happening now is more informative than what is
 * waiting, and the two states are a second apart in any case.
 */
export type AssistantOrbState = 'idle' | 'thinking' | 'attention'

const orbState = computed((): AssistantOrbState => {
    if (busy.value) return 'thinking'

    return pending.value || unread.value ? 'attention' : 'idle'
})

/**
 * The same three states as the head plays them (`components/app/MiloHead.vue`).
 *
 * The package offers five persistent states and this portal can honestly report three.
 * `listening` and `speaking` are for a microphone and a voice; there is neither here,
 * and a head that mouthed words nobody is saying would be the first thing in this
 * product that lies about what it is doing.
 *
 * `attention` maps to `warning` — the package's "Concerned" clip — because that is what
 * the state means: a proposal is waiting for a decision, or an answer has not been read.
 * It is the only expression in the set that asks the person for something.
 */
const MILO_STATES: Record<AssistantOrbState, MiloState> = {
    idle:      'idle',
    thinking:  'thinking',
    attention: 'warning',
}

export const miloStateFor = (state: AssistantOrbState): MiloState => MILO_STATES[state]

export const useAssistant = () => ({
    open,
    threadId,
    busy,
    pending,
    unread,
    orbState,

    openDrawer:   () => { open.value = true },
    closeDrawer:  () => { open.value = false },
    toggleDrawer: () => { open.value = !open.value },

    /**
     * An answer has landed, and whether anybody was looking at it.
     *
     * The caller decides, because there are two places a conversation is drawn: the
     * drawer, which is being looked at exactly when it is open, and the full page,
     * where the reader is by definition already reading. An orb that said "unread"
     * about an answer somebody has open in front of them would be the one state that
     * teaches people to ignore it.
     */
    answered: (seen: boolean) => { unread.value = !seen },
})

/**
 * Whether this account gets Milo at all.
 *
 * TWO permissions, and the second one is this portal's own answer rather than the
 * service's. `chat:use` is the assistant's door everywhere and is all the reception
 * portal asks for; here it is joined by `housekeeping:inspect`, which is the permission
 * every supervisor screen in this portal is already behind — the sheet, the teams, the
 * board, the statistics.
 *
 * The reason is what the two people holding this portal are doing. A housekeeper has a
 * telephone on a trolley and a round on it: everything Milo could tell her about the
 * morning is the list she is looking at, and everything he could DO for her needs
 * inspect anyway (`propose_housekeeping_assign` checks it in the service). A supervisor
 * has the whole floor and the questions that go with it. An orb the housekeeper could
 * press would be a conversation that answers "nimaš pravice" to most of what it invites
 * her to ask, and that is a worse thing to ship than no orb.
 *
 * It is a computed and not a call, because `GET /me` answers after the top bar has
 * already drawn: an account's permissions are unknown for a moment, and unknown means
 * false here — an orb that appears a beat late is right, and one that appears and then
 * vanishes is a portal arguing with itself.
 */
export const useAssistantAvailable = () => {
    const appStore = useAppStore()

    return computed((): boolean =>
        appStore.can(Permission.ChatUse) && appStore.can(Permission.HousekeepingInspect))
}

/**
 * Runs the callback after the assistant has EXECUTED something, so the screen
 * underneath admits it happened.
 *
 * A proposal confirmed in the drawer changes the very page it was confirmed over — a
 * round handed to somebody else redraws the sheet, a rota change redraws the week.
 * Without this the screen would go on showing what it had loaded before the change, and
 * the supervisor would reload by hand to believe it.
 *
 * The signal is a counter on the app store rather than a DOM event: a component that
 * mounts after an execution must not run its reload for a change it already fetched,
 * and a watcher on a number gives that for nothing.
 */
export const onAssistantExecuted = (callback: () => void) => {
    const appStore = useAppStore()

    const stop = watch(() => appStore.assistantExecutions, () => callback())

    onUnmounted(stop)
}
